import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box, Button, Typography } from "@mui/joy";
import { useLocation, useNavigate } from "react-router-dom";
import type { CanvasDataSource, CanvasNavigationConfig } from "../../../types";
import { useAccumulatedViewRows } from "../../../hooks/useAccumulatedViewRows";
import type { AccumulatedViewRows } from "../../../hooks/useAccumulatedViewRows";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import type { RenderableElement } from "../CanvasRenderer";
import { useCanvasRuntime } from "../CanvasRuntimeContext";
import { createViewService } from "../../../services/viewService";
import {
  fetchHierarchyExpansion,
  hierarchyExpansionRequests,
  HierarchyExpansionRequest,
  HierarchyRows,
} from "./hierarchyExpansion";
import { openCanvasNavigation } from "../canvasNavigation";
import { CanvasHierarchyGraphView } from "./CanvasHierarchyGraphView";
import {
  buildHierarchyGraph,
  HierarchyConfig,
  HierarchyNode,
} from "./hierarchyGraph";

interface Props {
  element: RenderableElement;
  dataSources: CanvasDataSource[];
  canvasId?: string | null;
}
const AUTO_LOAD_ROWS = 1000;

function SourceRows({
  alias,
  viewId,
  canvasId,
  elementId,
  onRows,
  starting,
  keyField,
}: {
  alias: string;
  viewId: string;
  canvasId: string;
  elementId: string;
  starting?: (string | number)[];
  keyField?: string;
  onRows: (
    alias: string,
    rows: AccumulatedViewRows["rows"],
    partial: boolean,
    blocked: boolean,
  ) => void;
}) {
  const runtime = useCanvasRuntime();
  const filters =
    runtime?.interactionBus
      .getRuntimeFilters({ canvasId, elementId, dataSourceAlias: alias })
      .flatMap((entry) => entry.filters) || [];
  const sourceFilters = [
    ...filters,
    ...(starting?.length && keyField
      ? [
          {
            field: keyField,
            operator: starting.length === 1 ? ("eq" as const) : ("in" as const),
            value: starting.length === 1 ? starting[0] : starting,
          },
        ]
      : []),
  ];
  const result = useAccumulatedViewRows(viewId, {
    canvasId,
    filters: sourceFilters.length ? sourceFilters : undefined,
    filterLogic: "AND",
  });
  const { rows, hasMore, isLoading, isLoadingMore, error, loadMore } = result;
  useEffect(() => {
    onRows(
      alias,
      rows,
      hasMore || isLoading || isLoadingMore || !!error,
      !!error,
    );
  }, [alias, rows, hasMore, isLoading, isLoadingMore, error, onRows]);
  useEffect(() => {
    if (
      !error &&
      hasMore &&
      !isLoading &&
      !isLoadingMore &&
      rows.length >= 100 &&
      rows.length < AUTO_LOAD_ROWS
    )
      loadMore();
  }, [error, hasMore, isLoading, isLoadingMore, rows.length, loadMore]);
  return (
    <Box>
      {error ? (
        <Alert color="danger">
          {error instanceof Error
            ? error.message
            : "Hierarchy View could not be loaded"}
          <Button onClick={() => result.retry?.()}>Retry {alias}</Button>
        </Alert>
      ) : null}
      <Typography level="body-sm">
        {alias}: {rows.length} of {result.totalCount ?? "unknown"} rows
        {isLoading || isLoadingMore ? " — loading" : ""}
      </Typography>
      {hasMore && (
        <Button disabled={isLoading || isLoadingMore} onClick={loadMore}>
          Load more {alias}
        </Button>
      )}
    </Box>
  );
}

function BoundHierarchy({
  element,
  dataSources,
  canvasId,
}: Props & { canvasId: string }) {
  const config = element.props.hierarchy_config as unknown as HierarchyConfig;
  const aliases = useMemo(
    () => [
      ...new Set([
        ...config.nodes.map((node) => node.data_source_alias),
        ...config.relationships.flatMap((relation) =>
          relation.link_data_source_alias
            ? [relation.link_data_source_alias]
            : [],
        ),
      ]),
    ],
    [config],
  );
  const [loaded, setLoaded] = useState<
    Record<
      string,
      { rows: AccumulatedViewRows["rows"]; partial: boolean; blocked: boolean }
    >
  >({});
  const onRows = useCallback(
    (
      alias: string,
      rows: AccumulatedViewRows["rows"],
      partial: boolean,
      blocked: boolean,
    ) => {
      setLoaded((previous) =>
        previous[alias]?.rows === rows &&
        previous[alias]?.partial === partial &&
        previous[alias]?.blocked === blocked
          ? previous
          : { ...previous, [alias]: { rows, partial, blocked } },
      );
    },
    [],
  );
  const [extra, setExtra] = useState<HierarchyRows>({});
  const allRows = useMemo(
    () =>
      Object.fromEntries(
        aliases.map((alias) => [
          alias,
          loaded[alias]?.blocked
            ? []
            : [...(loaded[alias]?.rows || []), ...(extra[alias] || [])],
        ]),
      ),
    [loaded, extra, aliases],
  );
  const graph = useMemo(
    () => buildHierarchyGraph(config, allRows),
    [config, allRows],
  );
  const scopeAbort = useRef(new AbortController());
  useEffect(() => {
    const controller = new AbortController();
    scopeAbort.current = controller;
    return () => controller.abort();
  }, []);
  const [expandingKey, setExpandingKey] = useState<string | null>(null);
  const [retryNode, setRetryNode] = useState<{
    key: string;
    continuation: boolean;
  } | null>(null);
  const [expansionError, setExpansionError] = useState<string | null>(null);
  const completed = useRef(new Set<string>());
  const [remaining, setRemaining] = useState<
    Record<string, HierarchyExpansionRequest[]>
  >({});
  const runtime = useCanvasRuntime();
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const onOpen = async (node: HierarchyNode) => {
    const action = config.nodes.find(
      (mapping) => mapping.data_source_alias === node.alias,
    )?.node_click_action;
    if (action?.type !== "NAVIGATE" || !action.config || !runtime) return;
    try {
      setNavigationError(null);
      await openCanvasNavigation(action.config as CanvasNavigationConfig, {
        workspaceId: runtime.workspaceId,
        currentRowId: String(node.recordId),
        selectedRowId: String(node.recordId),
        authFetch,
        navigate,
        returnPath: `${location.pathname}${location.search}`,
      });
    } catch (error) {
      setNavigationError(
        error instanceof Error
          ? error.message
          : "Destination unavailable or access denied.",
      );
    }
  };
  const expand = async (node: HierarchyNode, continuation = false) => {
    if (
      expandingKey ||
      !runtime ||
      (!continuation && completed.current.has(node.key))
    )
      return;
    setExpandingKey(node.key);
    setExpansionError(null);
    setRetryNode({ key: node.key, continuation });
    try {
      const requests = continuation
        ? remaining[node.key] || []
        : hierarchyExpansionRequests(config, node, allRows);
      const result = await fetchHierarchyExpansion(
        requests,
        async (alias, request) => {
          const source = dataSources.find((candidate) =>
            [
              candidate.id,
              candidate.name,
              (candidate as { alias?: string }).alias,
            ].includes(alias),
          );
          if (!source?.view_id)
            throw new Error("Hierarchy requires configured Views.");
          const inherited = runtime.interactionBus
            .getRuntimeFilters({
              canvasId,
              elementId: element.id,
              dataSourceAlias: alias,
            })
            .flatMap((entry) => entry.filters);
          return createViewService(
            authFetch,
            runtime.workspaceId,
          ).queryViewData(String(source.view_id), {
            canvasId,
            page: request.page,
            pageSize: 100,
            filterLogic: "AND",
            filters: [
              ...inherited,
              {
                field: request.field,
                operator: request.values.length === 1 ? "eq" : "in",
                value:
                  request.values.length === 1
                    ? request.values[0]
                    : request.values,
              },
            ],
          });
        },
        10,
        scopeAbort.current.signal,
      );
      setExtra((previous) =>
        Object.fromEntries(
          aliases.map((alias) => [
            alias,
            [...(previous[alias] || []), ...(result.rows[alias] || [])],
          ]),
        ),
      );
      if (!result.remaining.length) completed.current.add(node.key);
      setRemaining((previous) => ({
        ...previous,
        [node.key]: result.remaining,
      }));
    } catch (error) {
      if (!scopeAbort.current.signal.aborted)
        setExpansionError(
          error instanceof Error ? error.message : "Hierarchy expansion failed",
        );
    } finally {
      if (!scopeAbort.current.signal.aborted) setExpandingKey(null);
    }
  };
  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography level="title-md">
        {String(element.props.title || "Hierarchy")}
      </Typography>
      {expandingKey && (
        <Typography role="status">Loading hierarchy children…</Typography>
      )}
      {expansionError && (
        <Alert color="danger">
          {expansionError}
          <Button
            onClick={() => {
              const node = graph.nodes.find(
                (candidate) => candidate.key === retryNode?.key,
              );
              if (node) expand(node, retryNode?.continuation);
            }}
          >
            Retry expansion
          </Button>
        </Alert>
      )}
      {Object.entries(remaining)
        .filter(([, requests]) => requests.length)
        .map(([key]) => {
          const node = graph.nodes.find((candidate) => candidate.key === key);
          return node ? (
            <Button
              key={key}
              disabled={!!expandingKey}
              onClick={() => expand(node, true)}
            >
              Load remaining children {node.label}
            </Button>
          ) : null;
        })}
      {navigationError && <Alert color="danger">{navigationError}</Alert>}
      {aliases.map((alias) => {
        const source = dataSources.find((candidate) =>
          [
            candidate.id,
            candidate.name,
            (candidate as { alias?: string }).alias,
          ].includes(alias),
        )!;
        return (
          <SourceRows
            key={alias}
            alias={alias}
            viewId={String(source.view_id)}
            canvasId={canvasId}
            elementId={element.id}
            onRows={onRows}
            starting={config.starting_items?.[alias]}
            keyField={
              config.nodes.find((node) => node.data_source_alias === alias)
                ?.id_field
            }
          />
        );
      })}
      <CanvasHierarchyGraphView
        onExpand={(node) => expand(node)}
        expandingKey={expandingKey}
        canOpen={(node) =>
          config.nodes.find(
            (mapping) => mapping.data_source_alias === node.alias,
          )?.node_click_action?.type === "NAVIGATE"
        }
        graph={graph}
        config={config}
        partial={
          Object.values(config.starting_items || {}).some(
            (values) => values.length > 0,
          ) || aliases.some((alias) => !loaded[alias] || loaded[alias].partial)
        }
        onOpen={
          config.nodes.some(
            (mapping) => mapping.node_click_action?.type === "NAVIGATE",
          )
            ? onOpen
            : undefined
        }
      />
    </Box>
  );
}

export function CanvasHierarchyRenderer(props: Props) {
  const runtime = useCanvasRuntime();
  const config = props.element.props.hierarchy_config as unknown as
    HierarchyConfig | undefined;
  const canvasId = props.canvasId ?? runtime?.canvasId;
  const validArrays =
    Array.isArray(config?.nodes) && Array.isArray(config?.relationships);
  const aliases =
    config && validArrays
      ? [
          ...config.nodes.map((node) => node.data_source_alias),
          ...config.relationships.flatMap((relation) =>
            relation.link_data_source_alias
              ? [relation.link_data_source_alias]
              : [],
          ),
        ]
      : [];
  if (
    !canvasId ||
    !validArrays ||
    !config?.nodes.length ||
    !config.relationships.length ||
    aliases.some(
      (alias) =>
        !props.dataSources.some(
          (source) =>
            [
              source.id,
              source.name,
              (source as { alias?: string }).alias,
            ].includes(alias) && source.view_id,
        ),
    )
  )
    return (
      <Alert color="warning">
        Hierarchy requires configured Views and relationships.
      </Alert>
    );
  const scope = JSON.stringify([
    runtime?.workspaceId,
    canvasId,
    config,
    props.dataSources,
    aliases.map((alias) =>
      runtime?.interactionBus.getRuntimeFilters({
        canvasId,
        elementId: props.element.id,
        dataSourceAlias: alias,
      }),
    ),
  ]);
  return <BoundHierarchy key={scope} {...props} canvasId={canvasId} />;
}
